import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Settings, 
  User, 
  Mail, 
  Calendar,
  Shield,
  Trophy,
  Heart,
  MessageCircle,
  Route,
  Save,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const vehicleTypes = [
  { value: 'oldtimer', label: 'Oldtimer / Classic Car' },
  { value: 'caravan', label: 'Caravan' },
  { value: 'motorhome', label: 'Motorhome' },
];

const SettingsPage = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    bio: '',
    favorite_vehicle_type: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/users/me/profile`);
      setProfile(response.data);
      setFormData({
        bio: response.data.bio || '',
        favorite_vehicle_type: response.data.favorite_vehicle_type || ''
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/users/me/profile`, formData);
      setProfile(response.data);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const getReputationBadge = (reputation) => {
    if (reputation >= 100) return { label: 'Expert Navigator', color: 'bg-amber-500' };
    if (reputation >= 50) return { label: 'Road Master', color: 'bg-blue-500' };
    if (reputation >= 20) return { label: 'Explorer', color: 'bg-green-500' };
    return { label: 'Newcomer', color: 'bg-muted' };
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-full bg-background overflow-auto">
      <div className="p-6 md:p-8 max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="font-display text-3xl font-bold text-foreground flex items-center gap-3">
            <Settings className="w-8 h-8 text-primary" />
            Settings
          </h1>
          <p className="text-muted-foreground mt-2">
            Manage your account and profile.
          </p>
        </div>

        <div className="space-y-6">
          {/* Profile Section */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-display text-xl text-foreground flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Profile
              </CardTitle>
              <CardDescription>
                Your public profile information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between py-3 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center">
                    <span className="text-xl font-semibold text-primary">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-foreground text-lg">{user?.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {profile && (
                        <Badge className={`${getReputationBadge(profile.reputation).color} text-white`}>
                          <Trophy className="w-3 h-3 mr-1" />
                          {getReputationBadge(profile.reputation).label}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                {profile && (
                  <div className="text-right">
                    <p className="text-2xl font-display font-bold text-primary">{profile.reputation}</p>
                    <p className="text-xs text-muted-foreground">Reputation Points</p>
                  </div>
                )}
              </div>

              {/* Stats */}
              {profile && (
                <div className="grid grid-cols-3 gap-4 py-4 border-b border-border">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                      <Route className="w-4 h-4" />
                    </div>
                    <p className="text-xl font-display font-bold text-foreground">{profile.shared_routes_count}</p>
                    <p className="text-xs text-muted-foreground">Shared Routes</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                      <Heart className="w-4 h-4" />
                    </div>
                    <p className="text-xl font-display font-bold text-foreground">{profile.total_likes_received}</p>
                    <p className="text-xs text-muted-foreground">Likes Received</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                      <MessageCircle className="w-4 h-4" />
                    </div>
                    <p className="text-xl font-display font-bold text-foreground">{profile.total_comments}</p>
                    <p className="text-xs text-muted-foreground">Comments</p>
                  </div>
                </div>
              )}

              {/* Editable Profile Fields */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="bio" className="text-foreground">Bio</Label>
                  <Textarea
                    id="bio"
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    placeholder="Tell others about yourself and your vehicles..."
                    className="bg-muted/50 border-border min-h-[80px]"
                    data-testid="bio-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vehicleType" className="text-foreground">Favorite Vehicle Type</Label>
                  <Select 
                    value={formData.favorite_vehicle_type} 
                    onValueChange={(value) => setFormData({ ...formData, favorite_vehicle_type: value })}
                  >
                    <SelectTrigger className="bg-muted/50 border-border" data-testid="favorite-vehicle-select">
                      <SelectValue placeholder="Select your favorite" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicleTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                  data-testid="save-profile-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Profile
                </Button>
              </div>

              <div className="space-y-3 pt-4 border-t border-border">
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <Label className="text-muted-foreground w-20">Email</Label>
                  <span className="text-foreground">{user?.email}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <Label className="text-muted-foreground w-20">Joined</Label>
                  <span className="text-foreground">
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Reputation Info */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-display text-xl text-foreground flex items-center gap-2">
                <Trophy className="w-5 h-5 text-primary" />
                Reputation System
              </CardTitle>
              <CardDescription>
                How your reputation is calculated
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center p-2 bg-muted/30 rounded">
                  <span className="text-muted-foreground">Each like received</span>
                  <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">+10 points</Badge>
                </div>
                <div className="flex justify-between items-center p-2 bg-muted/30 rounded">
                  <span className="text-muted-foreground">Each shared route</span>
                  <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">+5 points</Badge>
                </div>
                <div className="flex justify-between items-center p-2 bg-muted/30 rounded">
                  <span className="text-muted-foreground">Each comment posted</span>
                  <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">+2 points</Badge>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-border">
                <h4 className="text-sm font-medium text-foreground mb-3">Reputation Levels</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-muted text-muted-foreground">Newcomer</Badge>
                    <span className="text-xs text-muted-foreground">0 - 19 points</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-500 text-white">Explorer</Badge>
                    <span className="text-xs text-muted-foreground">20 - 49 points</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-500 text-white">Road Master</Badge>
                    <span className="text-xs text-muted-foreground">50 - 99 points</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-amber-500 text-white">Expert Navigator</Badge>
                    <span className="text-xs text-muted-foreground">100+ points</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* About Section */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-display text-xl text-foreground flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                About Curvio
              </CardTitle>
              <CardDescription>
                Application information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Version</span>
                  <span className="text-foreground">1.1.0</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Map Provider</span>
                  <span className="text-foreground">OpenStreetMap</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Routing Engine</span>
                  <span className="text-foreground">OSRM</span>
                </div>
              </div>
              <div className="pt-4 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  Curvio provides specialized navigation for classic cars, caravans, and motorhomes 
                  with gradient, bend, and narrow road warnings to ensure safe journeys.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
